/*
 * <cautiva-split-hero> — drives per-panel image rotation in
 * sections/cautiva-split-hero.liquid, plus the optional full-bleed video
 * moment that plays once on first viewport entry and then hands off to
 * dual-panel rotation for the page lifetime.
 *
 * Two timing tracks share one section-level pause machine:
 *
 *   1. Dual-panel rotation — each panel (panel--left, panel--right) with
 *      ≥2 images cycles through its slides independently. The second
 *      rotating panel starts offset by interval/2 so the two never pulse
 *      in lockstep. A panel with 1 image stays static (this is how
 *      asymmetric rotation is achieved — fewer images on one side).
 *
 *   2. Video moment — desktop only (ADR-012 disables autoplay on mobile).
 *      Plays once on first viewport entry. Capped at video_duration
 *      seconds via setTimeout; ends earlier if the file is shorter (native
 *      `ended` event). Either path hands off to dual-panel rotation and
 *      sets introPlayed = true so the moment never re-fires for the page.
 *
 * Pause signals (any one stops everything):
 *   - Pointer hover (panel-scoped for dual-panel)
 *   - Keyboard focus (panel-scoped for dual-panel)
 *   - User pressed the section toggle (data-cautiva-toggle)
 *   - Section scrolled out of viewport (IntersectionObserver)
 *   - Tab not visible (document.visibilitychange)
 *   - prefers-reduced-motion (hard stop — no rotation, no video play)
 *
 * WCAG 2.2.2: section-level toggle is the explicit-user-control. Its
 * aria-label state-switches between "Pausar rotación" and "Pausar video"
 * based on which track is active.
 *
 * WCAG 1.4.2: video is hardcoded muted + playsinline; no sound surface.
 */

class CautivaSplitHero extends HTMLElement {
  #abortController;
  #panels = [];
  #section;
  #toggle;
  #liveRegion;
  #reducedMotionMQ;
  #mobileMQ;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const intervalSeconds = Number.parseFloat(this.dataset.rotationInterval) || 6;
    const interval = Math.max(5, Math.min(12, intervalSeconds)) * 1000;
    const videoDurationSeconds = Number.parseFloat(this.dataset.videoDuration) || 8;
    const videoDuration = Math.max(3, Math.min(30, videoDurationSeconds)) * 1000;

    this.#reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#mobileMQ = window.matchMedia('(max-width: 749px)');

    this.#toggle = this.querySelector('[data-cautiva-toggle]');
    this.#liveRegion = this.querySelector('[data-cautiva-live-region]');
    const videoContainer = this.querySelector('.cautiva-split-hero__video');
    const videoEl = videoContainer?.querySelector('video') ?? null;

    this.#section = {
      videoDuration,
      interval,
      videoEl,
      hasVideo: videoEl !== null,
      isMobile: this.#mobileMQ.matches,
      reducedMotion: this.#reducedMotionMQ.matches,
      intersecting: false,
      visible: !document.hidden,
      userPaused: false,
      videoActive: false,
      introPlayed: false,
      videoBlocked: false,
      videoMaxTimer: null,
    };

    const panelElements = this.querySelectorAll('.cautiva-split-hero__panel');
    let rotatingIndex = 0;
    this.#panels = Array.from(panelElements).map((panelEl) => {
      const slides = Array.from(panelEl.querySelectorAll('.cautiva-split-hero__slide'));
      if (slides.length > 0) {
        slides[0].classList.add('is-active');
      }
      const sideRotates = slides.length >= 2;
      if (!sideRotates) {
        return this.#noopPanel();
      }
      // Stagger only applies to the second rotating panel — a single-side
      // rotation starts on the full interval (no half-cycle offset).
      const stagger = rotatingIndex > 0;
      rotatingIndex += 1;
      return this.#createPanel(panelEl, slides, stagger, interval, signal);
    });

    this.#reducedMotionMQ.addEventListener('change', () => {
      this.#section.reducedMotion = this.#reducedMotionMQ.matches;
      this.#panels.forEach((p) => p.setReducedMotion(this.#section.reducedMotion));
      this.#syncSection();
    }, { signal });

    this.#mobileMQ.addEventListener('change', () => {
      this.#section.isMobile = this.#mobileMQ.matches;
      this.#syncSection();
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      this.#section.visible = !document.hidden;
      this.#panels.forEach((p) => p.setVisible(this.#section.visible));
      this.#syncSection();
    }, { signal });

    const observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);
      this.#section.intersecting = isIntersecting;
      this.#panels.forEach((p) => p.setIntersecting(isIntersecting));
      this.#syncSection();
    }, { threshold: 0.1 });
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());

    if (videoEl) {
      videoEl.addEventListener('ended', () => this.#handleVideoEnded(), { signal });
      videoEl.addEventListener('error', () => this.#handleVideoFailure(), { signal });
    }

    if (this.#toggle) {
      this.#toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#section.userPaused = !this.#section.userPaused;
        this.#panels.forEach((p) => p.setUserPaused(this.#section.userPaused));
        this.#syncSection();
      }, { signal });
    }

    this.#updateToggleLabel();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#panels.forEach((p) => p.destroy());
    this.#panels = [];
    this.#clearVideoMaxTimer();
  }

  #noopPanel() {
    return {
      setReducedMotion: () => {},
      setVisible: () => {},
      setIntersecting: () => {},
      setUserPaused: () => {},
      setVideoActive: () => {},
      destroy: () => {},
    };
  }

  #createPanel(panelEl, slides, stagger, interval, signal) {
    const state = {
      current: 0,
      timer: null,
      userPaused: false,
      hovered: false,
      focused: false,
      intersecting: false,
      visible: !document.hidden,
      reducedMotion: this.#reducedMotionMQ.matches,
      videoActive: false,
      started: false,
    };

    const shouldRun = () =>
      !state.userPaused &&
      !state.hovered &&
      !state.focused &&
      !state.videoActive &&
      state.intersecting &&
      state.visible &&
      !state.reducedMotion;

    const advance = () => {
      slides[state.current].classList.remove('is-active');
      state.current = (state.current + 1) % slides.length;
      slides[state.current].classList.add('is-active');
    };

    const tick = () => {
      advance();
      state.timer = window.setTimeout(tick, interval);
    };

    const sync = () => {
      if (shouldRun()) {
        if (state.timer === null) {
          const initialDelay = state.started || !stagger ? interval : interval / 2;
          state.timer = window.setTimeout(tick, initialDelay);
          state.started = true;
        }
      } else if (state.timer !== null) {
        window.clearTimeout(state.timer);
        state.timer = null;
      }
    };

    panelEl.addEventListener('mouseenter', () => {
      state.hovered = true;
      sync();
    }, { signal });
    panelEl.addEventListener('mouseleave', () => {
      state.hovered = false;
      sync();
    }, { signal });
    panelEl.addEventListener('focusin', () => {
      state.focused = true;
      sync();
    }, { signal });
    panelEl.addEventListener('focusout', (event) => {
      if (!panelEl.contains(event.relatedTarget)) {
        state.focused = false;
        sync();
      }
    }, { signal });

    sync();

    return {
      setReducedMotion: (value) => {
        state.reducedMotion = value;
        sync();
      },
      setVisible: (value) => {
        state.visible = value;
        sync();
      },
      setIntersecting: (value) => {
        state.intersecting = value;
        sync();
      },
      setUserPaused: (value) => {
        state.userPaused = value;
        sync();
      },
      setVideoActive: (value) => {
        state.videoActive = value;
        sync();
      },
      destroy: () => {
        if (state.timer !== null) {
          window.clearTimeout(state.timer);
          state.timer = null;
        }
      },
    };
  }

  #sectionRunning() {
    return (
      this.#section.intersecting &&
      this.#section.visible &&
      !this.#section.userPaused &&
      !this.#section.reducedMotion
    );
  }

  #syncSection() {
    const { hasVideo, isMobile, reducedMotion, introPlayed, videoBlocked } = this.#section;
    const videoEligible = hasVideo && !isMobile && !reducedMotion && !introPlayed && !videoBlocked;

    if (videoEligible && this.#sectionRunning() && !this.#section.videoActive) {
      this.#activateVideo();
    } else if (this.#section.videoActive && !this.#sectionRunning()) {
      this.#pauseVideoPlayback();
    } else if (this.#section.videoActive && this.#sectionRunning()) {
      this.#resumeVideoPlayback();
    } else if (!videoEligible && !this.#section.videoActive && this.dataset.active === 'video') {
      // SSR opens on the video view so the poster paints from frame 1.
      // When the video can't actually play here (mobile / reduced-motion /
      // autoplay-blocked / already-played), hand off to the panels view.
      this.dataset.active = 'panels';
    }

    this.#updateToggleLabel();
  }

  #activateVideo() {
    const { videoEl, videoDuration } = this.#section;
    if (!videoEl) return;
    this.#section.videoActive = true;
    this.dataset.active = 'video';
    this.#panels.forEach((p) => p.setVideoActive(true));
    if (videoEl.preload !== 'auto') {
      videoEl.preload = 'auto';
    }
    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => this.#handleVideoFailure());
    }
    this.#clearVideoMaxTimer();
    this.#section.videoMaxTimer = window.setTimeout(() => {
      this.#section.videoMaxTimer = null;
      this.#handleVideoEnded();
    }, videoDuration);
    this.#announce('Video iniciado');
  }

  #deactivateVideo() {
    if (!this.#section.videoActive) return;
    this.#section.videoActive = false;
    this.dataset.active = 'panels';
    this.#panels.forEach((p) => p.setVideoActive(false));
    const { videoEl } = this.#section;
    if (videoEl && !videoEl.paused) {
      videoEl.pause();
    }
    this.#clearVideoMaxTimer();
    this.#announce('Rotación reanudada');
  }

  #pauseVideoPlayback() {
    const { videoEl } = this.#section;
    if (videoEl && !videoEl.paused) {
      videoEl.pause();
    }
  }

  #resumeVideoPlayback() {
    const { videoEl } = this.#section;
    if (!videoEl || !videoEl.paused) return;
    if (this.#section.userPaused) return;
    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => this.#handleVideoFailure());
    }
  }

  #handleVideoEnded() {
    this.#section.introPlayed = true;
    this.#deactivateVideo();
    this.#updateToggleLabel();
  }

  #handleVideoFailure() {
    this.#section.videoBlocked = true;
    this.#section.introPlayed = true;
    this.#deactivateVideo();
    this.#updateToggleLabel();
  }

  #clearVideoMaxTimer() {
    if (this.#section.videoMaxTimer !== null) {
      window.clearTimeout(this.#section.videoMaxTimer);
      this.#section.videoMaxTimer = null;
    }
  }

  #announce(message) {
    if (!this.#liveRegion || !message) return;
    this.#liveRegion.textContent = message;
  }

  #updateToggleLabel() {
    if (!this.#toggle) return;
    const playing = !this.#section.userPaused && this.#sectionRunning();
    const inVideo = this.#section.videoActive;
    this.#toggle.setAttribute('aria-pressed', String(this.#section.userPaused));
    this.#toggle.dataset.state = playing ? 'playing' : 'paused';
    const playLabel = inVideo
      ? this.#toggle.dataset.labelPlayVideo
      : this.#toggle.dataset.labelPlayRotation;
    const pauseLabel = inVideo
      ? this.#toggle.dataset.labelPauseVideo
      : this.#toggle.dataset.labelPauseRotation;
    const nextLabel = this.#section.userPaused ? playLabel : pauseLabel;
    if (nextLabel) {
      this.#toggle.setAttribute('aria-label', nextLabel);
    }
  }
}

if (!customElements.get('cautiva-split-hero')) {
  customElements.define('cautiva-split-hero', CautivaSplitHero);
}
